import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import RaiseDebt from "./RaiseDebt";
import { ArrowLeft, Plus, Trash2, RefreshCw, TrendingDown } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const DEBT_TYPES = ["Working Capital", "Short Term Loan", "Long Term Loan", "Term Loan", "Overdraft", "Letter of Credit", "Buyers Credit"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "NZD", "SGD", "AED", "SAR"];
const PAYMENT_FREQUENCIES = ["Monthly", "Quarterly", "Semi-Annual", "Annual", "Bullet"];
const RATING_PROVIDERS = ["CRISIL", "ICRA", "CARE", "India Ratings", "Moody's", "S&P", "Fitch", "None"];
const RATINGS = ["AAA", "AA+", "AA", "AA-", "A+", "A", "A-", "BBB+", "BBB", "BBB-", "BB+", "BB", "BB-", "B", "C", "D", "NR"];

const DONUT_COLORS = ["#6366f1", "#22d3ee", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"];

const CURRENCY_SYMBOLS = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", JPY: "¥",
  AUD: "A$", CAD: "C$", CHF: "CHF ", NZD: "NZ$",
  SGD: "S$", AED: "AED ", SAR: "SAR "
};

const DEBT_BENCHMARK_URL = process.env.REACT_APP_DEBT_BENCHMARK_URL;
const SUPABASE_ANON      = process.env.REACT_APP_SUPABASE_ANON_KEY;

function debtCacheKey(debt) {
  const sig = debt.map(d =>
    `${d.provider}|${d.interest_rate}|${d.outstanding_balance}|${d.loan_amount}|${d.currency}`
  ).join(",");
  return `nebula_debt_benchmark_${btoa(unescape(encodeURIComponent(sig))).slice(0, 40)}`;
}

function formatDateInput(value) {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0,2)}-${digits.slice(2)}`;
  return `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4,8)}`;
}

function toISO(ddmmyyyy) {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return null;
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  return `${yyyy}-${mm}-${dd}`;
}

function fromISO(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function formatAmount(n, currency) {
  if (!n && n !== 0) return "0";
  const symbol = currency ? (CURRENCY_SYMBOLS[currency] || currency + " ") : "";
  const isINR = currency === "INR";
  if (isINR) {
    if (Math.abs(n) >= 10000000) return `${symbol}${(n / 10000000).toFixed(2)}Cr`;
    if (Math.abs(n) >= 100000)   return `${symbol}${(n / 100000).toFixed(2)}L`;
    if (Math.abs(n) >= 1000)     return `${symbol}${(n / 1000).toFixed(2)}K`;
    return `${symbol}${n.toFixed(0)}`;
  }
  if (Math.abs(n) >= 1000000) return `${symbol}${(n / 1000000).toFixed(2)}M`;
  if (Math.abs(n) >= 1000)    return `${symbol}${(n / 1000).toFixed(2)}K`;
  return `${symbol}${n.toFixed(2)}`;
}

export default function DebtManagement({ onBack, companyId, profile }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showRaiseDebt, setShowRaiseDebt] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  // ── Benchmark state ──
  const [benchmarkLoading,  setBenchmarkLoading]  = useState(false);
  const [benchmarkResult,   setBenchmarkResult]   = useState(null);
  const [benchmarkError,    setBenchmarkError]    = useState(null);
  const [showBenchmark,     setShowBenchmark]     = useState(false);

  const [form, setForm] = useState({
    debt_type: "", provider: "", currency: "INR",
    loan_amount: "", outstanding_balance: "", interest_rate: "",
    market_benchmark: "", start_date: "", maturity_date: "",
    payment_frequency: "", rating: "", rating_provider: ""
  });

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('debt_positions')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      if (error) console.error("Error loading debt:", error);
      else setRecords(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => setForm({
    debt_type: "", provider: "", currency: "INR",
    loan_amount: "", outstanding_balance: "", interest_rate: "",
    market_benchmark: "", start_date: "", maturity_date: "",
    payment_frequency: "", rating: "", rating_provider: ""
  });

  // ── Benchmark Analysis ──────────────────────────────────────────────────────
  const handleBenchmarkAnalysis = async () => {
    if (!allDebt.length) return;
    // Check cache first
  const cacheKey = debtCacheKey(allDebt);
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    setBenchmarkResult(JSON.parse(cached));
    setShowBenchmark(true);
    return;
  }

    setBenchmarkLoading(true);
    setBenchmarkError(null);
    setBenchmarkResult(null);
    setShowBenchmark(true);

    try {
      const debtPayload = allDebt.map(d => ({
        provider:     d.provider,
        interestRate: d.interest_rate,
        outstanding:  d.outstanding_balance || d.loan_amount,
        loanAmount:   d.loan_amount,
        rating:       d.rating,
        debt_type:    d.debt_type,
        currency:     d.currency,
        maturity_date: d.maturity_date,
      }));

      const res = await fetch(DEBT_BENCHMARK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${SUPABASE_ANON}`,
        },
        body: JSON.stringify({
          debt: debtPayload,
          companyProfile: profile || {},
        }),
      });

const text = await res.text();
if (!res.ok) throw new Error(`Benchmark error ${res.status}`);
const data = JSON.parse(text)
      if (data.error) throw new Error(data.error);
      setBenchmarkResult(data);
      
      // Cache against current loan signature
localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (e) {
      setBenchmarkError(e.message || "Failed to fetch benchmark analysis.");
    } finally {
      setBenchmarkLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.debt_type)          { alert("Debt type is required.");           return; }
    if (!form.provider)           { alert("Provider / Bank is required.");      return; }
    if (!form.currency)           { alert("Currency is required.");             return; }
    if (!form.loan_amount)        { alert("Loan amount is required.");          return; }
    if (!form.outstanding_balance){ alert("Outstanding balance is required.");  return; }
    if (!form.interest_rate)      { alert("Interest rate is required.");        return; }
    if (!form.market_benchmark)   { alert("Market benchmark is required.");     return; }
    if (!form.payment_frequency)  { alert("Payment frequency is required.");    return; }
    if (!form.start_date)         { alert("Start date is required.");           return; }
    if (!form.maturity_date)      { alert("Maturity date is required.");        return; }
    if (!form.rating)             { alert("Rating is required.");               return; }
    if (!form.rating_provider)    { alert("Rating provider is required.");      return; }

    if (parseFloat(form.outstanding_balance) > parseFloat(form.loan_amount)) {
      alert("Outstanding balance cannot be greater than the loan amount.");
      return;
    }

    setSaving(true);
    const payload = {
      company_id:          companyId,
      debt_type:           form.debt_type,
      provider:            form.provider,
      currency:            form.currency,
      loan_amount:         form.loan_amount ? parseFloat(form.loan_amount) : null,
      outstanding_balance: form.outstanding_balance ? parseFloat(form.outstanding_balance) : null,
      interest_rate:       form.interest_rate ? parseFloat(form.interest_rate) : null,
      market_benchmark:    form.market_benchmark ? parseFloat(form.market_benchmark) : null,
      start_date:          form.start_date ? toISO(form.start_date) : null,
      maturity_date:       form.maturity_date ? toISO(form.maturity_date) : null,
      payment_frequency:   form.payment_frequency,
      rating:              form.rating,
      rating_provider:     form.rating_provider,
    };

    try {
      if (editRecord) {
        const { error } = await supabase.from('debt_positions').update(payload).eq('id', editRecord.id);
        if (error) console.error("Update error:", error);
        setEditRecord(null);
      } else {
        const { error } = await supabase.from('debt_positions').insert(payload);
        if (error) console.error("Insert error:", error);
      }
      await loadData();
      resetForm();
      setShowForm(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (r) => {
    setEditRecord(r);
    setForm({
      debt_type:           r.debt_type || "",
      provider:            r.provider || "",
      currency:            r.currency || "INR",
      loan_amount:         r.loan_amount || "",
      outstanding_balance: r.outstanding_balance || "",
      interest_rate:       r.interest_rate || "",
      market_benchmark:    r.market_benchmark || "",
      start_date:          fromISO(r.start_date) || "",
      maturity_date:       fromISO(r.maturity_date) || "",
      payment_frequency:   r.payment_frequency || "",
      rating:              r.rating || "",
      rating_provider:     r.rating_provider || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this debt position?")) return;
    const { error } = await supabase.from('debt_positions').delete().eq('id', id);
    if (error) console.error("Delete error:", error);
    else await loadData();
  };

  // ── Chart data ──
  const allDebt = records.map(r => ({ ...r, source: "new" }));

  const pieData = (() => {
    const map = {};
    allDebt.forEach(d => {
      const type = d.debt_type || "Other";
      if (!map[type]) map[type] = 0;
      map[type] += parseFloat(d.outstanding_balance || d.loan_amount) || 0;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
  })();

  const barData = (() => {
    const map = {};
    allDebt.forEach(d => {
      const ccy = d.currency || "INR";
      if (!map[ccy]) map[ccy] = 0;
      map[ccy] += parseFloat(d.outstanding_balance || d.loan_amount) || 0;
    });
    return Object.entries(map).map(([currency, amount]) => ({ currency, amount })).filter(d => d.amount > 0);
  })();

  const totalDebt        = allDebt.reduce((sum, d) => sum + (parseFloat(d.loan_amount) || 0), 0);
  const totalOutstanding = allDebt.reduce((sum, d) => sum + (parseFloat(d.outstanding_balance) || parseFloat(d.loan_amount) || 0), 0);

  const wtdAvgRate = (() => {
    if (!allDebt.length) return 0;
    const totalOut = allDebt.reduce((sum, d) => sum + (parseFloat(d.outstanding_balance || d.loan_amount) || 0), 0);
    if (totalOut === 0) return 0;
    const wtdSum = allDebt.reduce((sum, d) => {
      const rate = parseFloat(d.interest_rate) || 0;
      const out  = parseFloat(d.outstanding_balance || d.loan_amount) || 0;
      return sum + (rate * out);
    }, 0);
    return wtdSum / totalOut;
  })();

  const daysUntil = (isoDate) => {
    if (!isoDate) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    return Math.round((new Date(isoDate) - today) / (1000 * 60 * 60 * 24));
  };

  if (showRaiseDebt) return <RaiseDebt onBack={() => setShowRaiseDebt(false)} companyId={companyId} />;

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Debt Management</h2>
          <p className="text-gray-400 text-sm">Complete view of your debt portfolio</p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">
            <RefreshCw className="w-4 h-4" />
          </button>
          {/* ── Benchmark Analysis Button ── */}
          <button
            onClick={handleBenchmarkAnalysis}
            disabled={benchmarkLoading || !allDebt.length}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 transition-all text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <TrendingDown className="w-4 h-4" />
            {benchmarkLoading ? "Analysing…" : "Benchmark Analysis"}
          </button>
          <button
            onClick={() => setShowRaiseDebt(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10 transition-all text-sm font-medium"
          >
            🏦 Raise Debt
          </button>
          <button
            onClick={() => { setShowForm(!showForm); setEditRecord(null); resetForm(); }}
            className="nebula-btn-primary flex items-center gap-2 text-sm px-4 py-2"
          >
            <Plus className="w-4 h-4" /> Add Debt
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Total Debt</p>
          <p className="text-2xl font-bold text-white">{formatAmount(totalDebt)}</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Outstanding</p>
          <p className="text-2xl font-bold text-red-400">{formatAmount(totalOutstanding)}</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Wtd Avg Rate</p>
          <p className="text-2xl font-bold text-amber-400">{isNaN(wtdAvgRate) ? "N/A" : `${wtdAvgRate.toFixed(2)}%`}</p>
          <p className="text-xs text-gray-500 mt-1">on outstanding debt</p>
        </div>
        <div className="nebula-card p-4 text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Total Facilities</p>
          <p className="text-2xl font-bold text-indigo-400">{allDebt.length}</p>
        </div>
      </div>

      {/* ── Benchmark Analysis Panel ── */}
      {showBenchmark && (
        <div className="nebula-card p-6 mb-6 border border-emerald-500/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-emerald-400 text-lg">📊</span>
              <h3 className="text-lg font-bold text-white">Benchmark Analysis</h3>
              <span className="text-xs text-gray-500">Powered by RBI MCLR + Rating Spread Data</span>
            </div>
            <button
              onClick={() => setShowBenchmark(false)}
              className="text-gray-500 hover:text-white text-xs px-3 py-1 rounded-lg border border-[#2a2d3a] transition-all"
            >
              Close
            </button>
          </div>

          {benchmarkLoading && (
            <div className="flex items-center justify-center py-12 gap-3">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-400 text-sm">Fetching MCLR rates and analysing your portfolio…</p>
            </div>
          )}

          {benchmarkError && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">
              {benchmarkError}
            </div>
          )}

          {benchmarkResult && !benchmarkLoading && (
            <>
              {/* MCLR Reference Strip */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Current Market Rates</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    RBI Repo: {benchmarkResult.repoRate}%
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                    SBI 1Y MCLR: {benchmarkResult.sbi1Y}%
                  </span>
                  {(benchmarkResult.mclrRates || [])
                    .filter(r => r.tenor === "1 Year" && r.bank_name !== "SBI")
                    .slice(0, 4)
                    .map(r => (
                      <span key={r.bank_name} className="px-3 py-1 rounded-full text-xs font-medium bg-[#1a1d26] text-gray-400 border border-[#2a2d3a]">
                        {r.bank_name}: {r.rate}%
                      </span>
                    ))
                  }
                </div>
              </div>

              {/* Per-loan summary */}
              {benchmarkResult.debtSummary?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Loan vs Market</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {benchmarkResult.debtSummary.map((d, i) => (
                      <div key={i} className={`p-3 rounded-xl border ${
                        d.overpriced
                          ? "bg-amber-500/5 border-amber-500/20"
                          : "bg-emerald-500/5 border-emerald-500/20"
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <div>
  <p className="text-sm font-semibold text-white">{d.provider}</p>
  <p className="text-xs text-gray-500 mt-0.5">{d.rating} rated · {d.outstanding > 0 ? `₹${(d.outstanding/100000).toFixed(1)}L outstanding` : ""}</p>
</div>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                     parseFloat(d.diff) > 0.25
  ? "bg-amber-500/20 text-amber-400"
  : parseFloat(d.diff) < -0.25
  ? "bg-emerald-500/20 text-emerald-400"
  : "bg-blue-500/20 text-blue-400"
                          }`}>
                           {parseFloat(d.diff) > 0.25 ? "ABOVE MARKET" : parseFloat(d.diff) < -0.25 ? "BELOW MARKET" : "AT MARKET"}
                          </span>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-400">
                          <span>Current: <span className="text-white font-medium">{d.rate}%</span></span>
                          <span>Fair: <span className="text-emerald-400 font-medium">{d.fairRate}%</span></span>
                          <span>Diff: <span className={`font-medium ${d.overpriced ? "text-amber-400" : "text-emerald-400"}`}>
                            {d.diff > 0 ? "+" : ""}{d.diff}%
                          </span></span>
                        </div>
                       {parseFloat(d.diff) > 0.25 && d.annualSaving > 0 && (
  <p className="text-xs text-amber-400 mt-1">
    Potential saving: {formatAmount(d.annualSaving, "INR")} p.a. if refinanced
  </p>
)}
{parseFloat(d.diff) < -0.25 && (
  <p className="text-xs text-emerald-400 mt-1">
    ✓ Favourable rate — {Math.abs(parseFloat(d.diff)).toFixed(2)}% below fair market value
  </p>
)}
{Math.abs(parseFloat(d.diff)) <= 0.25 && (
  <p className="text-xs text-blue-400 mt-1">
    Rate is aligned with current market pricing
  </p>
)}


                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Recommendation */}
              <div className="bg-[#0a0b0f] border border-[#2a2d3a] border-l-4 border-l-emerald-500 rounded-xl p-4">
                <p className="text-xs text-emerald-400 font-semibold uppercase tracking-wider mb-3">
                  ✦ Nebula AI Refinancing Recommendations
                </p>
                <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">
                  {benchmarkResult.recommendation}
                </p>
                <div className="text-sm text-gray-300 leading-relaxed space-y-2">
  {benchmarkResult.recommendation
    .replace(/\\\[[\s\S]*?\\\]/g, '')  // remove LaTeX block math
    .replace(/\\\([\s\S]*?\\\)/g, '')  // remove LaTeX inline math
    .split('\n')
    .filter(line => line.trim())
    .map((line, i) => {
      // Headers
      if (line.startsWith('### ')) return (
        <h4 key={i} className="text-white font-bold text-base mt-3 mb-1">{line.replace('### ', '')}</h4>
      );
      if (line.startsWith('## ')) return (
        <h3 key={i} className="text-white font-bold text-lg mt-4 mb-1">{line.replace('## ', '')}</h3>
      );
      // Numbered list
      if (/^\d+\./.test(line)) return (
        <div key={i} className="flex gap-2">
          <span className="text-emerald-400 font-bold shrink-0">{line.match(/^\d+\./)[0]}</span>
          <span dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
        </div>
      );
      // Bullet list
      if (line.startsWith('- ') || line.startsWith('* ')) return (
        <div key={i} className="flex gap-2">
          <span className="text-emerald-400 shrink-0">•</span>
          <span dangerouslySetInnerHTML={{ __html: line.replace(/^[-*]\s*/, '').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
        </div>
      );
      // Regular paragraph with bold support
      return (
        <p key={i} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
      );
    })
  }
</div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="nebula-card p-6">
          <h3 className="text-lg font-bold text-white mb-4">Debt Split by Type</h3>
          {pieData.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">Add debt positions to see the chart.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={65} outerRadius={105} paddingAngle={3}
                    label={({ name, percent }) => `${name.split(" ")[0]} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: "#4b5563", strokeWidth: 1 }}>
                    {pieData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val, name) => [formatAmount(val), name]}
                    contentStyle={{ background: "#12141a", border: "1px solid #2a2d3a", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                {pieData.map((d, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1 rounded-full border"
                    style={{ borderColor: DONUT_COLORS[i % DONUT_COLORS.length] + "40", background: DONUT_COLORS[i % DONUT_COLORS.length] + "15" }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="text-xs font-medium" style={{ color: DONUT_COLORS[i % DONUT_COLORS.length] }}>{d.name}</span>
                    <span className="text-xs text-gray-400">{formatAmount(d.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="nebula-card p-6">
          <h3 className="text-lg font-bold text-white mb-4">Debt Split by Currency</h3>
          {barData.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">Add debt positions to see the chart.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barData} barSize={32}>
                  <XAxis dataKey="currency" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={{ stroke: "#2a2d3a" }} tickLine={false} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatAmount(v)} width={70} />
                  <Tooltip formatter={(val, _, props) => [formatAmount(val, props.payload.currency), "Amount"]}
                    contentStyle={{ background: "#12141a", border: "1px solid #2a2d3a", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="amount" radius={[6,6,0,0]} name="Amount">
                    {barData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                {barData.map((d, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1 rounded-full border"
                    style={{ borderColor: DONUT_COLORS[i % DONUT_COLORS.length] + "40", background: DONUT_COLORS[i % DONUT_COLORS.length] + "15" }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="text-xs font-medium" style={{ color: DONUT_COLORS[i % DONUT_COLORS.length] }}>{d.currency}</span>
                    <span className="text-xs text-gray-400">{formatAmount(d.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="nebula-card p-6 mb-6">
          <h3 className="text-lg font-bold text-white mb-4">{editRecord ? "Edit Debt Position" : "Add New Debt Position"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Debt Type <span className="text-red-400">*</span></label>
              <select value={form.debt_type} onChange={e => setForm({...form, debt_type: e.target.value})} className="nebula-input text-sm">
                <option value="">Select type...</option>
                {DEBT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Provider / Bank <span className="text-red-400">*</span></label>
              <input value={form.provider} onChange={e => setForm({...form, provider: e.target.value})} className="nebula-input text-sm" placeholder="e.g. HDFC Bank" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Currency <span className="text-red-400">*</span></label>
              <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} className="nebula-input text-sm">
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Loan Amount <span className="text-red-400">*</span></label>
              <input type="number" value={form.loan_amount} onChange={e => setForm({...form, loan_amount: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 10000000" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Outstanding Balance <span className="text-red-400">*</span></label>
              <input type="number" value={form.outstanding_balance} onChange={e => setForm({...form, outstanding_balance: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 8000000" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Interest Rate % <span className="text-red-400">*</span></label>
              <input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 10.5" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Market Benchmark % <span className="text-red-400">*</span></label>
              <input type="number" step="0.01" value={form.market_benchmark} onChange={e => setForm({...form, market_benchmark: e.target.value})} className="nebula-input text-sm" placeholder="e.g. 9.5" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Payment Frequency <span className="text-red-400">*</span></label>
              <select value={form.payment_frequency} onChange={e => setForm({...form, payment_frequency: e.target.value})} className="nebula-input text-sm">
                <option value="">Select...</option>
                {PAYMENT_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Start Date (DD-MM-YYYY) <span className="text-red-400">*</span></label>
              <input value={form.start_date} onChange={e => setForm({...form, start_date: formatDateInput(e.target.value)})}
                className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Maturity Date (DD-MM-YYYY) <span className="text-red-400">*</span></label>
              <input value={form.maturity_date} onChange={e => setForm({...form, maturity_date: formatDateInput(e.target.value)})}
                className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Rating <span className="text-red-400">*</span></label>
              <select value={form.rating} onChange={e => setForm({...form, rating: e.target.value})} className="nebula-input text-sm">
                <option value="">Select...</option>
                {RATINGS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Rating Provider <span className="text-red-400">*</span></label>
              <select value={form.rating_provider} onChange={e => setForm({...form, rating_provider: e.target.value})} className="nebula-input text-sm">
                <option value="">Select...</option>
                {RATING_PROVIDERS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} disabled={saving} className="nebula-btn-primary text-sm px-6 py-2 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : editRecord ? "Update" : "Save"}
            </button>
            <button onClick={() => { setShowForm(false); setEditRecord(null); resetForm(); }}
              className="px-6 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Debt Details Table */}
      <div className="nebula-card overflow-hidden">
        <div className="px-6 py-4 border-b border-[#2a2d3a] flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Debt Details</h3>
          <span className="text-xs text-gray-500">{allDebt.length} facilit{allDebt.length !== 1 ? "ies" : "y"}</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : allDebt.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p>No debt positions yet. Click Add Debt to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#12141a] border-b border-[#2a2d3a]">
                  {["Type","Provider","Currency","Loan Amount","Outstanding","Interest Rate","Benchmark","Status","Start Date","Maturity","Days Left","Frequency","Rating","Rating By",""].map(h => (
                    <th key={h} className="text-left text-xs text-gray-500 uppercase py-3 px-4 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allDebt.map((r, i) => {
                  const rate      = parseFloat(r.interest_rate) || 0;
                  const benchmark = parseFloat(r.market_benchmark) || 9.5;
                  const aboveMarket = rate > benchmark;
                  const days = daysUntil(r.maturity_date);
                  return (
                    <tr key={i} className={`border-b border-[#2a2d3a]/40 ${i % 2 === 0 ? "" : "bg-[#12141a]/40"}`}>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-400">{r.debt_type}</span>
                      </td>
                      <td className="py-3 px-4 text-white font-medium whitespace-nowrap">{r.provider}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400">{r.currency || "INR"}</span>
                      </td>
                      <td className="py-3 px-4 text-white font-semibold whitespace-nowrap">{formatAmount(parseFloat(r.loan_amount), r.currency || "INR")}</td>
                      <td className="py-3 px-4 text-red-400 font-medium whitespace-nowrap">{formatAmount(parseFloat(r.outstanding_balance || r.loan_amount), r.currency || "INR")}</td>
                      <td className="py-3 px-4 text-gray-300">{rate > 0 ? `${rate}%` : "-"}</td>
                      <td className="py-3 px-4 text-gray-400">{benchmark > 0 ? `${benchmark}%` : "9.5%"}</td>
                      <td className="py-3 px-4">
                        {rate > 0 ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                            aboveMarket
                              ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                              : rate < benchmark
                              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                              : "bg-blue-500/20 text-blue-400 border-blue-500/30"
                          }`}>
                            {aboveMarket ? "ABOVE MARKET" : rate < benchmark ? "BELOW MARKET" : "AT MARKET"}
                          </span>
                        ) : "-"}
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{fromISO(r.start_date) || "-"}</td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{fromISO(r.maturity_date) || "-"}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {days !== null ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                            days <= 30  ? "bg-red-500/20 text-red-400 border-red-500/30" :
                            days <= 90  ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                                          "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                          }`}>{days}d</span>
                        ) : "-"}
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{r.payment_frequency || "-"}</td>
                      <td className="py-3 px-4">
                        {r.rating ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">{r.rating}</span>
                        ) : "-"}
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{r.rating_provider || "-"}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleEdit(r)} className="px-2 py-1 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/40 text-xs font-medium transition-all">Edit</button>
                          <button onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-300 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
